'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { logger } from '@/lib/logger';
import Link from 'next/link';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export default function PaymentSuccessPage() {
  const searchParams = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Confirming your payment...');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const matchId = searchParams.get('match_id');

    if (!sessionId) {
      setStatus('error');
      setMessage('Invalid payment confirmation');
      return;
    }

    async function confirmHire() {
      try {
        // Call our server to confirm the hire
        // This ensures match status + worker notification happen
        // even if the Stripe webhook is delayed
        const response = await fetch('/api/payments/confirm-hire', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionId,
            match_id: matchId,
          }),
        });

        if (response.ok) {
          setStatus('success');
          setMessage('Payment successful! The worker has been notified of your hire.');
        } else {
          // Even if confirm-hire fails, Stripe got the money
          // Webhook will catch up
          setStatus('success');
          setMessage('Payment received! Processing your hire — the worker will be notified shortly.');
        }
      } catch (error) {
        logger.error('Error confirming hire:', error);
        // Stripe redirected here = payment succeeded
        setStatus('success');
        setMessage('Payment received! Processing your hire — please check your matches shortly.');
      }
    }

    confirmHire();
  }, [searchParams]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-6 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
              <h1 className="mt-6 text-2xl font-bold">Confirming Payment</h1>
              <p className="mt-2 text-muted-foreground">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-500/10">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="mt-6 text-2xl font-bold">Payment Successful!</h1>
              <p className="mt-2 text-muted-foreground">{message}</p>
              <div className="mt-8 space-y-3">
                <Button asChild className="w-full h-12 text-base">
                  <Link href="/contractor/matches">Back to Matches</Link>
                </Button>
                <Button asChild variant="outline" className="w-full h-12 text-base">
                  <Link href="/dashboard">Go to Dashboard</Link>
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle className="h-10 w-10 text-destructive" />
              </div>
              <h1 className="mt-6 text-2xl font-bold">Payment Error</h1>
              <p className="mt-2 text-muted-foreground">{message}</p>
              <div className="mt-8 space-y-3">
                <Button asChild variant="outline" className="w-full h-12 text-base">
                  <Link href="/contractor/matches">Back to Matches</Link>
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
